import React, { useState } from "react";
import styled from "styled-components";
import ResortDetail from "./ResortDetail";
import { Settings2, ChevronDown, Clock, MapPin } from "lucide-react";

const ControlPanel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 320px;
  background: rgba(255, 255, 255, 1);
  padding: 16px;
  border-radius: 7px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  font-family: sans-serif;
  z-index: 10;
  min-height: 100px;

  @media (max-width: 800px) {
    position: fixed;
    bottom: 0;
    left: 0;
    top: auto;
    width: 100%;
    height: 40vh;
    max-height: 40vh;
    border-radius: 12px 12px 0 0;
    padding: 20px;
    padding-top: 0px;
    box-sizing: border-box;
    overflow-y: auto;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ButtonGroup = styled.div`
  position: absolute;
  top: 10px;
  right: 8px;
  display: flex;
  padding: 0px;
  padding-right: 8px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  opacity: 0.6;
  color: ${({ $active }) => ($active ? "#0077ff" : "#999")};
  transition: color 0.2s ease;
  padding-right: 0px;

  &:hover {
    color: ${({ $active }) => ($active ? "#005fcc" : "#666")};
  }
`;

const AdvancedSettings = styled.div`
  padding: 20px 0px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SelectLabel = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-top: 10px;
`;

const StyledSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  padding-right: 36px; /* space for arrow */
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eee;
  font-size: 0.9rem;
  color: #333;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #ccc;
  }

  &:focus {
    outline: none;
    border-color: #0077ff;
    box-shadow: 0 0 0 2px rgba(0, 119, 255, 0.2);
  }
`;

const ArrowIcon = styled(ChevronDown)`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
  width: 16px;
  height: 16px;
`;

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #0077ff;
    cursor: pointer;
  }
`;

export default function Controls({
  basemap,
  setBasemap,
  showLines,
  setShowLines,
  showMarkers,
  setShowMarkers,
  showTimeline,
  setShowTimeline,
  show3D,
  setShow3D,
  resortMetaData,
  onBack,
  resortStats,
  onHoverLocation,
  resortRuns,
  isAdmin,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <ControlPanel>
      <ButtonGroup>
        <IconButton
          onClick={() => setShowTimeline((prev) => !prev)}
          $active={showTimeline}
        >
          <Clock size={18} />
        </IconButton>
        <IconButton
          onClick={() => setShowMarkers((prev) => !prev)}
          $active={showMarkers}
        >
          <MapPin size={18} />
        </IconButton>
        {isAdmin && (
          <IconButton
            onClick={() => setShowAdvanced(!showAdvanced)}
            $active={showAdvanced}
          >
            <Settings2 size={20} />
          </IconButton>
        )}
      </ButtonGroup>
      <ResortDetail
        resortMetaData={resortMetaData}
        resortStats={resortStats}
        resortRuns={resortRuns}
        onBack={onBack}
        onHoverLocation={onHoverLocation}
      />
      {showAdvanced && (
        <AdvancedSettings>
          <SelectLabel>Map style:</SelectLabel>
          <StyledSelectWrapper>
            <StyledSelect
              value={basemap}
              onChange={(e) => setBasemap(e.target.value)}
            >
              <option value="carto">Carto Light</option>
              <option value="maptiler">MapTiler Winter</option>
              <option value="osm">OpenStreetMap</option>
            </StyledSelect>
            <ArrowIcon />
          </StyledSelectWrapper>

          <SelectLabel>Layers:</SelectLabel>

          <CheckboxWrapper>
            <input
              type="checkbox"
              checked={showLines}
              onChange={() => setShowLines((prev) => !prev)}
            />
            Show Track Lines
          </CheckboxWrapper>

          <CheckboxWrapper>
            <input
              type="checkbox"
              checked={show3D}
              onChange={() => setShow3D((prev) => !prev)}
            />
            3D Terrain
          </CheckboxWrapper>
        </AdvancedSettings>
      )}
    </ControlPanel>
  );
}
